"use client";
import { CommonImage } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import React, { Suspense, useState } from "react";

export interface VideoSectionProps {
  heading?: string;
  description?: string;
  videoId?: string;
  thumbnailSrc?: string;
  className?: string;
}

const VideoSection: React.FC<VideoSectionProps> = ({
  heading = "",
  description = "",
  videoId = "",
  thumbnailSrc = "",
  className = "",
}) => {
  const [playVideo, setPlayVideo] = useState(false);
  const videoSrc = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

  return (
    <section className={`bg-secondary py-10 sm:py-20 xl:py-16 ${className}`}>
      <div className="scontainer gap-10 w-full">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-10 justify-between items-center">
          <h2 className="text-center lg:text-left font-semibold text-secondary-foreground w-6/7 lg:w-3/7">
            {heading}
          </h2>
          {description && (
            <p className="text-white/80 lg:w-2/7 sm:w-4/7 w-6/7 text-center lg:text-left">
              {description}
            </p>
          )}
        </div>

        <div className="lg:col-span-5 flex flex-col items-center justify-center gap-6 w-full">
          <Card className="border-0 rounded-none bg-transparent mt-4 w-full">
            <CardContent className="p-0">
              {/* Below `lg` there was no height/aspect-ratio class at all - the
                  box collapsed to the <img>'s intrinsic 1000x1000 (square)
                  attributes instead of the actual 16:9 thumbnail, cropping
                  the promo graphic into a squarish, off-looking card. */}
              <div className="w-full aspect-[16/9] lg:aspect-auto lg:h-[47vw] xl:h-[35rem] rounded-lg sm:rounded-xl bg-gray-800 overflow-hidden relative">
                <Suspense fallback={<Skeleton className="w-full h-full" />}>
                  {playVideo ? (
                    <iframe
                      width="560"
                      height="315"
                      src={videoSrc}
                      title="YouTube video player"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      type="button"
                      aria-label="Play video"
                      className="w-full h-full cursor-pointer relative"
                      onClick={() => setPlayVideo(true)}
                    >
                      <CommonImage
                        src={thumbnailSrc}
                        width={1000}
                        height={1000}
                        alt="Video Thumbnail"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg
                          className="w-16 h-16 text-white opacity-90 bg-red-600 shadow-2xl rounded-full z-20"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </button>
                  )}
                </Suspense>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
